import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { clearSessionCache } from "@/lib/api";
import { createClient } from "@/lib/supabase/client";
import { hasSupabaseEnv } from "@/lib/supabase/env";

export type AuthProfile = {
  full_name: string;
  avatar_url: string;
  role: string;
  status: string;
};

type AuthContextValue = {
  loading: boolean;
  userEmail: string;
  userId: string;
  profile: AuthProfile | null;
  enabledFeatures: string[];
  previewMode: boolean;
  refresh: () => Promise<void>;
  /** Reload feature flags from DB (e.g. after super admin changes access). */
  refreshFeatures: () => Promise<void>;
};

const previewProfile: AuthProfile = {
  full_name: "Preview User",
  avatar_url: "",
  role: "super_admin",
  status: "active",
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [userEmail, setUserEmail] = useState("preview@example.com");
  const [userId, setUserId] = useState("preview-user");
  const [profile, setProfile] = useState<AuthProfile | null>(previewProfile);
  const [enabledFeatures, setEnabledFeatures] = useState<string[]>([]);
  const previewMode = !hasSupabaseEnv();

  const refresh = useCallback(async () => {
    if (previewMode) {
      setProfile(previewProfile);
      setUserEmail("preview@example.com");
      setUserId("preview-user");
      setEnabledFeatures([]);
      setLoading(false);
      return;
    }

    const supabase = createClient();
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      clearSessionCache();
      setProfile(null);
      setUserEmail("");
      setUserId("");
      setEnabledFeatures([]);
      setLoading(false);
      return;
    }

    setUserEmail(user.email ?? "");
    setUserId(user.id);

    let { data: row } = await supabase
      .from("admin_users")
      .select("full_name, avatar_url, role, status")
      .eq("id", user.id)
      .single();

    if (!row) {
      await supabase.from("admin_users").upsert(
        {
          id: user.id,
          full_name: (user.user_metadata?.full_name as string | undefined) ?? user.email ?? "User",
          primary_email: user.email ?? "",
          avatar_url: (user.user_metadata?.avatar_url as string | undefined) ?? "",
          role: "admin",
          updated_at: new Date().toISOString(),
        },
        { onConflict: "id" },
      );

      const refreshed = await supabase
        .from("admin_users")
        .select("full_name, avatar_url, role, status")
        .eq("id", user.id)
        .single();

      row = refreshed.data ?? row;
    }

    if (row) {
      setProfile({
        full_name: row.full_name ?? "User",
        avatar_url: row.avatar_url ?? "",
        role: row.role ?? "admin",
        status: row.status ?? "active",
      });

      if (row.role !== "super_admin") {
        const { data: featureRows } = await supabase
          .from("admin_features")
          .select("feature_key")
          .eq("admin_id", user.id)
          .eq("enabled", true);

        setEnabledFeatures((featureRows ?? []).map((r) => r.feature_key));
      } else {
        setEnabledFeatures([]);
      }
    }

    setLoading(false);
  }, [previewMode]);

  const refreshFeatures = useCallback(async () => {
    if (previewMode || !hasSupabaseEnv()) return;

    const supabase = createClient();
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      setEnabledFeatures([]);
      return;
    }

    const { data: row } = await supabase
      .from("admin_users")
      .select("role")
      .eq("id", user.id)
      .single();

    if (!row || row.role === "super_admin") {
      setEnabledFeatures([]);
      return;
    }

    const { data: featureRows } = await supabase
      .from("admin_features")
      .select("feature_key")
      .eq("admin_id", user.id)
      .eq("enabled", true);

    setEnabledFeatures((featureRows ?? []).map((r) => r.feature_key));
  }, [previewMode]);

  useEffect(() => {
    if (previewMode) return;

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        void refreshFeatures();
      }
    };

    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [previewMode, refreshFeatures]);

  useEffect(() => {
    void refresh();

    if (previewMode) return;

    const supabase = createClient();
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN" || event === "SIGNED_OUT" || event === "USER_UPDATED") {
        void refresh();
      }
    });

    return () => subscription.unsubscribe();
  }, [refresh, previewMode]);

  const value = useMemo(
    () => ({
      loading,
      userEmail,
      userId,
      profile,
      enabledFeatures,
      previewMode,
      refresh,
      refreshFeatures,
    }),
    [loading, userEmail, userId, profile, enabledFeatures, previewMode, refresh, refreshFeatures],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
